import { configureStore, Reducer } from '@reduxjs/toolkit';
import _ from 'lodash';
import { TypedUseSelectorHook, useSelector } from 'react-redux';
import {
  createTransform,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  PersistConfig,
} from 'redux-persist';
import persistReducer from 'redux-persist/lib/persistReducer';
import persistStore from 'redux-persist/es/persistStore';
import { rootReducer } from 'careand-redux/rootReducer';
import storage from 'redux-persist/lib/storage';
import { api } from './api/ApiThunks';
import { safeAreaListener } from './listeners/SafeAreaListener';
import { navigationListener } from './listeners/NavigationListener';
import { CombinedState } from 'redux';
import { NavigationState, Stack, Stacks } from './slices/ui/navigation';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import config from 'config';
import localforage from 'localforage';
import { makeInitialUIState, UIState } from './slices/ui/ui';
import { RHFFormsState } from './slices/rhfForms';

export type RootReducerState = typeof rootReducer extends Reducer<CombinedState<infer S>>
  ? S
  : never;

// Prevents navigation page persisted scrolls from being persisted on redux-persist
const removeNavigationScroll = createTransform((inboundState: unknown, key) => {
  if (key === 'navigation') {
    const state = _.cloneDeep(inboundState) as NavigationState;
    Object.values(Stacks).forEach((key) => {
      state.stacks[key as Stack].state.forEach((it) => {
        it.scroll = undefined;
      });
    });
    return state;
  }
  return inboundState;
});

//ignores forms that are marked as transient
const importPersistedForms = createTransform((inboundState: unknown, key) => {
  if (key !== 'rhfForms') {
    return inboundState;
  }
  const formState = inboundState as RHFFormsState;
  for (let form in formState) {
    if (formState[form]?.$$$transient) {
      delete formState[form];
    }
  }
  return formState;
});

const importPersistedUIState = createTransform((inboundState: unknown, key) => {
  if (key === 'ui') {
    const newState = Object.assign({}, makeInitialUIState(), {
      assistants: (inboundState as UIState)?.assistants ?? [],
    });
    return newState;
  }
  return inboundState;
});

const PERSIST_KEY = 'rootv4';

export async function purgeStorage() {
  await localforage.clear();
  await persistor.purge();
  console.log('Purging Storage');
}

const persistConfig: PersistConfig<RootReducerState> = {
  key: PERSIST_KEY,
  debug: false, //config.ENVIRONMENT === 'production' && import.meta.env.PROD,
  storage,
  throttle: 1000,
  whitelist: [
    'authentication',
    'rhfForms',
    //'patients', make sure the patients are refreshed when reloading the portal
    'booking',
    'registration',
    'payments',
    'device',
    'ui',
    'actions',
  ],
  transforms: [removeNavigationScroll, importPersistedUIState, importPersistedForms],
  stateReconciler: autoMergeLevel2,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const endpointsThatReturnBlob = _.compact(
  [
    ...[
      api.Documents.previewDocumentFromHtml,
      api.Documents.getDocumentFile,
      api.Documents.getDocumentThumbnail,
      api.Resource.getPhoto,
      api.HealthRecords.previewReferral,
      api.HealthRecords.previewPrescription,
      api.HealthRecords.exportImmunizations,
      api.HealthRecords.getNotesAttachment,
      api.Physicians.getPhysicianSignature,
      api.Invoices.getInvoicePdf,
      api.Invoices.getReceiptPdf,
      api.Resource.getResource,
      api.Chat.getChatFile,
    ].map((it) => it.fulfilled),
  ].map((it) => it.type),
);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          ...endpointsThatReturnBlob,
        ],
      },
    })
      .prepend(safeAreaListener.middleware)
      .prepend(navigationListener.middleware),

  devTools: !(config.ENVIRONMENT === 'production' && import.meta.env.PROD),
});
export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
